import React, { useState } from "react";
import { api, upload, downloadPost } from "./api";
import { DecisionFields, type Detail, type Order } from "./Authorization";
import { useProblems, FieldProblem, invalid } from "./form-check";
import { pathBRules } from "./checks";

/**
 * Path B (build plan §9.1 #4): a remote provider. One-click packet PDF, the
 * Teams consult (Config's link only; nothing about the student goes in it),
 * then the returned signed PDF is attached and its decision recorded.
 * RN and up; the packet is a PHI export.
 */
export function PathB({ d, onDone }: { d: Detail; onDone: (d: Detail) => void }): React.ReactElement | null {
  const [decision, setDecision] = useState("Authorized");
  const [order, setOrder] = useState<Order>(d.order);
  const [notes, setNotes] = useState("");
  const [provider, setProvider] = useState({ name: "", credential: "" });
  const [file, setFile] = useState<File | null>(null);
  // Once uploaded, a retry of the decision reuses the same attached PDF.
  const [docKey, setDocKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { problems, check } = useProblems(pathBRules({ decision, order, notes, attached: Boolean(file || docKey), providerName: provider.name, providerCredential: provider.credential }));
  if (!d.can.attach && !d.can.packet) return null;
  const key = encodeURIComponent(d.med.medKey);

  async function packet() {
    setError(null);
    const e = await downloadPost(`/api/authorizations/${key}/packet`, "provider-packet.pdf");
    if (e) setError(e);
    else setMsg("Packet downloaded. Send it to the provider through the program's secure route.");
  }

  function teams(e: React.MouseEvent) {
    // The link opens at once (no pop-up blocker); the server records that it was opened.
    void api("POST", `/api/authorizations/${key}/teams`).then((r) => {
      if (!r.ok) setError(r.data.error ?? "Couldn't record the consult.");
    });
    void e;
  }

  async function attach() {
    setError(null);
    if (!check()) return;
    setBusy(true);
    let attachedKey = docKey;
    if (!attachedKey && file) {
      const up = await upload<{ document: { docKey: string } }>(`/api/authorizations/${key}/attachment?name=${encodeURIComponent(file.name)}`, file);
      if (!up.ok) {
        setBusy(false);
        setError(up.data.error ?? "The PDF didn't upload.");
        return;
      }
      attachedKey = up.data.document.docKey;
      setDocKey(attachedKey);
    }
    const r = await api<{ detail: Detail }>("POST", `/api/authorizations/${key}/path-b`, { docKey: attachedKey, providerName: provider.name, providerCredential: provider.credential, decision, order, notes });
    setBusy(false);
    if (r.ok) onDone(r.data.detail);
    else setError(`${r.data.error ?? "That didn't save."} The signed PDF is attached; fix the details and record again.`);
  }

  const attached = d.authorizations.filter((a) => a.captureMethod === "Upload");
  return (
    <section id="path-b" className="section-card">
      <h3 className="text-base font-semibold">Path B — remote provider</h3>
      <div className="mt-2 flex flex-wrap gap-2">
        {d.can.packet && <button type="button" className="btn-secondary" onClick={packet}>Provider packet (PDF)</button>}
        {d.can.attach &&
          (d.teamsLink ? (
            <a className="btn-secondary" href={d.teamsLink} target="_blank" rel="noreferrer noopener" onClick={teams}>Open Teams consult</a>
          ) : (
            <button type="button" className="btn-secondary" disabled title="Teams link not set in Config">Open Teams consult</button>
          ))}
      </div>
      {d.can.attach && !d.teamsLink && <p className="mt-1 text-xs text-slate-500">Teams link not set in Config (Contacts › Remote medical provider).</p>}
      {msg && <p className="mt-2 text-sm text-emerald-800">{msg}</p>}
      {d.can.attach && (
        <fieldset className="mt-4">
          <legend className="text-sm font-semibold">Attach the signed authorization</legend>
          {attached.length > 0 && <p className="text-xs text-slate-500">{attached.length} signed PDF(s) already attached.</p>}
          <input id="pb-file" type="file" accept="application/pdf" className="mt-1 block text-sm" {...invalid("pb-file", problems)} onChange={(e) => { setFile(e.target.files?.[0] ?? null); setDocKey(null); }} />
          <p className="text-xs text-slate-500">PDF only, 15 MB at most.</p>
          <FieldProblem id="pb-file" problems={problems} />
          <p className="mt-2 text-sm">From the signed document:</p>
          <div className="mt-1 grid gap-2 sm:grid-cols-2">
            <div><label htmlFor="pb-pn" className="field-label">Provider's name</label><input id="pb-pn" className="field-input" {...invalid("pb-pn", problems)} value={provider.name} onChange={(e) => setProvider({ ...provider, name: e.target.value })} /><FieldProblem id="pb-pn" problems={problems} /></div>
            <div><label htmlFor="pb-pc" className="field-label">Credential</label><input id="pb-pc" className="field-input" {...invalid("pb-pc", problems)} value={provider.credential} onChange={(e) => setProvider({ ...provider, credential: e.target.value })} /><FieldProblem id="pb-pc" problems={problems} /></div>
          </div>
          <DecisionFields d={d} decision={decision} setDecision={setDecision} order={order} setOrder={setOrder} notes={notes} setNotes={setNotes} idp="pb" problems={problems} />
          {error && <p className="field-error" role="alert">{error}</p>}
          <button type="button" className="btn-primary mt-3" disabled={busy} onClick={attach}>Attach and record the decision</button>
        </fieldset>
      )}
      {!d.can.attach && error && <p className="field-error" role="alert">{error}</p>}
    </section>
  );
}
